import React, { useState, useEffect } from 'react'
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Alert,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem as SelectMenuItem,
  Grid
} from '@mui/material'
import {
  Visibility,
  CheckCircle,
  Cancel,
  Edit,
  MoreVert,
  Pending,
  Refresh,
  Assignment,
  Send,
  Undo
} from '@mui/icons-material'
import { useSnackbar } from 'notistack'
import { format, parseISO } from 'date-fns'
import requestService from '../../../services/requestService'

const RequestList = ({ onEdit, onView, statusFilter: propStatusFilter, onRefresh }) => {
  const { enqueueSnackbar } = useSnackbar()
  
  const [loading, setLoading] = useState(false)
  const [requests, setRequests] = useState([])
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [totalCount, setTotalCount] = useState(0)
  const [anchorEl, setAnchorEl] = useState(null)
  const [selectedRequest, setSelectedRequest] = useState(null)
  const [actionDialog, setActionDialog] = useState({ open: false, type: '', data: {} })
  
  // Approval dialog state
  const [openApprovalDialog, setOpenApprovalDialog] = useState(false)
  const [approvalData, setApprovalData] = useState({ action: '', remarks: '' })
  
  // Filters
  const [statusFilter, setStatusFilter] = useState(propStatusFilter || 'ALL')
  const [typeFilter, setTypeFilter] = useState('ALL')
  const [searchQuery, setSearchQuery] = useState('')

  useEffect(() => {
    // Update statusFilter when propStatusFilter changes
    if (propStatusFilter) {
      setStatusFilter(propStatusFilter)
    }
  }, [propStatusFilter])

  useEffect(() => {
    loadRequests()
  }, [page, rowsPerPage, statusFilter, typeFilter, searchQuery])

  const loadRequests = async () => {
    setLoading(true)
    try {
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        requestType: typeFilter !== 'ALL' ? typeFilter : undefined,
        search: searchQuery || undefined
      }

      // Handle special "APPROVED" filter that includes multiple statuses
      if (statusFilter === 'APPROVED') {
        // Don't set status param, we'll filter on frontend for approved statuses
        // Approved means: PROCESSING, READY, ISSUED, COMPLETED
      } else if (statusFilter !== 'ALL') {
        params.status = statusFilter
      }

      const response = await requestService.getRequests(params)
      
      if (response.data.success) {
        let requests = response.data.data

        // Apply frontend filtering for APPROVED status
        if (statusFilter === 'APPROVED') {
          requests = requests.filter(request => 
            ['PROCESSING', 'READY', 'ISSUED', 'COMPLETED'].includes(request.status)
          )
        }

        setRequests(requests)
        setTotalCount(response.data.pagination?.total || requests.length)
      }
    } catch (error) {
      enqueueSnackbar('Failed to load requests', { variant: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const handleMenuOpen = (event, request) => {
    console.log('🔍 Opening menu for request:', request?._id, request?.status)
    setAnchorEl(event.currentTarget)
    setSelectedRequest(request)
  }

  const handleMenuClose = () => {
    console.log('🔒 Closing menu, selectedRequest:', selectedRequest?._id)
    setAnchorEl(null)
    // Don't clear selectedRequest here - keep it for action dialog
    // setSelectedRequest(null)
  }

  const handleApproval = async () => {
    if (!selectedRequest || !approvalData.action) return
    
    setLoading(true)
    try {
      let response
      if (approvalData.action === 'APPROVE') {
        // For approval, we assign the request to the current user
        // Try multiple ways to get current user ID
        let assignedTo = null
        
        // Method 1: From localStorage mrd_user
        try {
          const currentUser = JSON.parse(localStorage.getItem('mrd_user') || '{}')
          assignedTo = currentUser._id || currentUser.id
        } catch (e) {
          console.log('Could not get user from mrd_user:', e)
        }
        
        // Method 2: From localStorage user (alternative key)
        if (!assignedTo) {
          try {
            const currentUser = JSON.parse(localStorage.getItem('user') || '{}')
            assignedTo = currentUser._id || currentUser.id
          } catch (e) {
            console.log('Could not get user from user:', e)
          }
        }
        
        // Method 3: Create a dummy user ID for testing (remove this in production)
        if (!assignedTo) {
          console.log('No user found, using dummy ID for testing')
          // You can replace this with a real user ID from your database
          assignedTo = '507f1f77bcf86cd799439011' // Dummy ObjectId for testing
        }
        
        console.log('Assigning request to user:', assignedTo)
        
        response = await requestService.assignRequest(selectedRequest._id, { 
          assignedTo: assignedTo,
          remarks: approvalData.remarks || 'Request approved and assigned'
        })
      } else {
        response = await requestService.rejectRequest(selectedRequest._id, { 
          rejectionReason: approvalData.remarks || 'Request rejected'
        })
      }

      if (response.data.success) {
        enqueueSnackbar(`Request ${approvalData.action.toLowerCase()}ed successfully`, { variant: 'success' })
        loadRequests()
        onRefresh && onRefresh()
      }
    } catch (error) {
      console.error('Approval error:', error)
      enqueueSnackbar(
        error.response?.data?.message || `Failed to ${approvalData.action.toLowerCase()} request`,
        { variant: 'error' }
      )
    } finally {
      setLoading(false)
      setOpenApprovalDialog(false)
      setApprovalData({ action: '', remarks: '' })
    }
  }

  const handleAction = async (actionType, data = {}) => {
    if (!selectedRequest) {
      console.error('❌ No request selected for action:', actionType)
      enqueueSnackbar('No request selected', { variant: 'error' })
      return
    }

    setLoading(true)
    try {
      console.log(`🔄 Performing action: ${actionType}`, { requestId: selectedRequest._id, data })
      
      let response
      switch (actionType) {
        case 'assign':
          response = await requestService.assignRequest(selectedRequest._id, data)
          break
        case 'fulfill':
          console.log('📋 Fulfilling request with data:', data)
          response = await requestService.fulfillRequest(selectedRequest._id, data)
          break
        case 'receive':
          response = await requestService.receiveRequest(selectedRequest._id, data)
          break
        case 'reject':
          response = await requestService.rejectRequest(selectedRequest._id, data)
          break
        case 'close':
          response = await requestService.closeRequest(selectedRequest._id, data)
          break
        case 'return':
          response = await requestService.returnRequest(selectedRequest._id, data)
          break
        case 'reopen':
          response = await requestService.reopenRequest(selectedRequest._id, data)
          break
        default:
          throw new Error('Unknown action type')
      }

      if (response.data.success) {
        enqueueSnackbar(`Request ${actionType}ed successfully`, { variant: 'success' })
        loadRequests()
        onRefresh && onRefresh()
      } else {
        console.error(`❌ ${actionType} failed:`, response.data)
        enqueueSnackbar(response.data.message || `Failed to ${actionType} request`, { variant: 'error' })
      }
    } catch (error) {
      console.error(`❌ Error ${actionType}ing request:`, error)
      enqueueSnackbar(
        error.response?.data?.message || `Failed to ${actionType} request`,
        { variant: 'error' }
      )
    } finally {
      setLoading(false)
      setActionDialog({ open: false, type: '', data: {} })
      setSelectedRequest(null) // Clear selected request after action completes
    }
  }

  const getStatusChip = (status) => {
    const config = {
      PENDING: { color: 'warning', icon: <Pending fontSize="small" /> },
      PROCESSING: { color: 'info', icon: <Assignment fontSize="small" /> },
      READY: { color: 'primary', icon: <CheckCircle fontSize="small" /> },
      ISSUED: { color: 'success', icon: <Send fontSize="small" /> },
      COMPLETED: { color: 'success', icon: <CheckCircle fontSize="small" /> },
      REJECTED: { color: 'error', icon: <Cancel fontSize="small" /> }
    }
    
    const { color, icon } = config[status] || { color: 'default', icon: null }
    
    return (
      <Chip
        label={status}
        color={color}
        size="small"
        icon={icon}
      />
    )
  }

  const getPriorityChip = (priority) => {
    const config = {
      LOW: { color: 'default' },
      MEDIUM: { color: 'primary' },
      HIGH: { color: 'warning' },
      URGENT: { color: 'error' }
    }
    
    return (
      <Chip
        label={priority}
        color={config[priority]?.color || 'default'}
        size="small"
        variant="outlined"
      />
    )
  }

  const canPerformAction = (action, request) => {
    switch (action) {
      case 'assign':
        return request.status === 'PENDING'
      case 'fulfill':
        return ['PROCESSING', 'READY'].includes(request.status)
      case 'receive':
        return request.status === 'ISSUED'
      case 'reject':
        return ['PENDING', 'PROCESSING'].includes(request.status)
      case 'close':
        return request.status === 'READY'
      case 'reopen':
        return ['COMPLETED', 'REJECTED'].includes(request.status)
      default:
        return false
    }
  }

  const getTimeStatus = (request) => {
    if (!request.requiredByDate) return null
    
    const now = new Date()
    const requiredBy = new Date(request.requiredByDate)
    const daysDiff = Math.ceil((requiredBy - now) / (1000 * 3600 * 24))
    
    if (daysDiff < 0) {
      return { type: 'error', message: `${Math.abs(daysDiff)}d overdue` }
    } else if (daysDiff <= 2) {
      return { type: 'warning', message: `Due in ${daysDiff}d` }
    }
    return null
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" fontWeight={600}>
          File Requests ({totalCount})
        </Typography>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={loadRequests}
        >
          Refresh
        </Button>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by file number or requester..."
            />
          </Grid>
          
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <SelectMenuItem value="ALL">All Status</SelectMenuItem>
                <SelectMenuItem value="PENDING">Pending</SelectMenuItem>
                <SelectMenuItem value="APPROVED">Approved</SelectMenuItem>
                <SelectMenuItem value="PROCESSING">Processing</SelectMenuItem>
                <SelectMenuItem value="READY">Ready</SelectMenuItem>
                <SelectMenuItem value="ISSUED">Issued</SelectMenuItem>
                <SelectMenuItem value="COMPLETED">Completed</SelectMenuItem>
                <SelectMenuItem value="REJECTED">Rejected</SelectMenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Request Type</InputLabel>
              <Select
                value={typeFilter}
                label="Request Type"
                onChange={(e) => setTypeFilter(e.target.value)}
              >
                <SelectMenuItem value="ALL">All Types</SelectMenuItem>
                <SelectMenuItem value="LEGAL">Legal</SelectMenuItem>
                <SelectMenuItem value="INSURANCE">Insurance</SelectMenuItem>
                <SelectMenuItem value="RESEARCH">Research</SelectMenuItem>
                <SelectMenuItem value="PATIENT_COPY">Patient Copy</SelectMenuItem>
                <SelectMenuItem value="AUDIT">Audit</SelectMenuItem>
                <SelectMenuItem value="OTHER">Other</SelectMenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* Requests Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Request Details</TableCell>
              <TableCell>File/Patient</TableCell>
              <TableCell>Requester</TableCell>
              <TableCell>Priority</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Requested Date</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {requests.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Alert severity="info">
                    No requests found
                  </Alert>
                </TableCell>
              </TableRow>
            ) : (
              requests.map((request) => (
                <TableRow key={request._id} hover>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        {request.requestType?.replace(/_/g, ' ')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {request.purpose?.slice(0, 50)}...
                      </Typography>
                    </Box>
                  </TableCell>
                  
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        {request.mrdFiles?.[0]?.ipdNumber || 'N/A'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {request.mrdFiles?.[0]?.patientName || 'N/A'}
                      </Typography>
                    </Box>
                  </TableCell>
                  
                  <TableCell>
                    <Box>
                      <Typography variant="body2">
                        {request.requestedBy}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {request.requestorType?.replace(/_/g, ' ')}
                      </Typography>
                    </Box>
                  </TableCell>
                  
                  <TableCell>
                    {getPriorityChip(request.priority)}
                  </TableCell>
                  
                  <TableCell>
                    {getStatusChip(request.status)}
                  </TableCell>
                  
                  <TableCell>
                    <Box>
                      <Typography variant="body2">
                        {request.requestDate ? format(parseISO(request.requestDate), 'MMM dd, yyyy') : '-'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {request.requestDate ? format(parseISO(request.requestDate), 'HH:mm') : ''}
                      </Typography>
                      {(() => {
                        const timeStatus = getTimeStatus(request)
                        return timeStatus && (
                          <Chip
                            label={timeStatus.message}
                            color={timeStatus.type}
                            size="small"
                            sx={{ mt: 0.5, display: 'block', width: 'fit-content' }}
                          />
                        )
                      })()}
                    </Box>
                  </TableCell>
                  
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() => onView ? onView(request) : null}
                        >
                          <Visibility fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      
                      {request.status === 'PENDING' && (
                        <>
                          <Tooltip title="Approve">
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => {
                                setSelectedRequest(request)
                                setApprovalData({ action: 'APPROVE', remarks: '' })
                                setOpenApprovalDialog(true)
                              }}
                            >
                              <CheckCircle fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          
                          <Tooltip title="Reject">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => {
                                setSelectedRequest(request)
                                setApprovalData({ action: 'REJECT', remarks: '' })
                                setOpenApprovalDialog(true)
                              }}
                            >
                              <Cancel fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                      
                      {request.status === 'ISSUED' && (
                        <Tooltip title="Return Files">
                          <IconButton
                            size="small"
                            color="success"
                            onClick={() => {
                              setSelectedRequest(request)
                              setActionDialog({
                                open: true,
                                type: 'return',
                                data: { remarks: '' }
                              })
                            }}
                          >
                            <Undo fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, request)}
                      >
                        <MoreVert fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10))
            setPage(0)
          }}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </TableContainer>

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => {
          if (selectedRequest) {
            onEdit && onEdit(selectedRequest)
          }
          handleMenuClose()
        }}>
          <ListItemIcon>
            <Edit fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        
        <MenuItem onClick={() => {
          console.log('🎯 Fulfill menu clicked, selectedRequest:', selectedRequest?._id)
          if (selectedRequest && canPerformAction('fulfill', selectedRequest)) {
            console.log('✅ Opening fulfill dialog')
            setActionDialog({ 
              open: true, 
              type: 'fulfill', 
              data: { requestId: selectedRequest._id } 
            })
          } else {
            console.log('❌ Cannot perform fulfill action')
          }
          handleMenuClose()
        }}>
          <ListItemIcon>
            <CheckCircle fontSize="small" />
          </ListItemIcon>
          <ListItemText>Fulfill</ListItemText>
        </MenuItem>
      </Menu>

      {/* Approval Dialog */}
      <Dialog open={openApprovalDialog} onClose={() => setOpenApprovalDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {approvalData.action === 'APPROVE' ? 'Approve Request' : 'Reject Request'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Remarks"
              value={approvalData.remarks}
              onChange={(e) => setApprovalData(prev => ({ ...prev, remarks: e.target.value }))}
              placeholder={`Enter remarks for ${approvalData.action.toLowerCase()}ing this request...`}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenApprovalDialog(false)}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            color={approvalData.action === 'APPROVE' ? 'success' : 'error'}
            onClick={handleApproval}
          >
            {approvalData.action === 'APPROVE' ? 'Approve' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Action Dialog */}
      <Dialog 
        open={actionDialog.open} 
        onClose={() => {
          setActionDialog({ open: false, type: '', data: {} })
          setSelectedRequest(null)
        }} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>
          {actionDialog.type === 'fulfill' && 'Fulfill Request'}
          {actionDialog.type === 'assign' && 'Assign Request'}
          {actionDialog.type === 'receive' && 'Receive Request'}
          {actionDialog.type === 'close' && 'Close Request'}
          {actionDialog.type === 'return' && 'Return Files'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Notes"
              value={actionDialog.data.notes || ''}
              onChange={(e) => setActionDialog(prev => ({ 
                ...prev, 
                data: { ...prev.data, notes: e.target.value } 
              }))}
              placeholder={
                actionDialog.type === 'return' 
                  ? 'Enter return remarks...' 
                  : `Enter notes for ${actionDialog.type}ing this request...`
              }
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setActionDialog({ open: false, type: '', data: {} })
            setSelectedRequest(null)
          }}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={() => handleAction(actionDialog.type, actionDialog.data)}
          >
            {actionDialog.type?.charAt(0).toUpperCase() + actionDialog.type?.slice(1)}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default RequestList